import React, { useState, useEffect, useRef } from 'react';
import { bankStatementsApi, bankCandidatesApi } from '../api';

const TABS = [
  { key: 'UEBERSICHT', label: 'Übersicht' },
  { key: 'NEW_CONTRACT_CANDIDATE', label: 'Neue Verträge' },
  { key: 'EXISTING_CONTRACT', label: 'Erkannte Verträge' },
  { key: 'REVIEW_REQUIRED', label: 'Unsichere Treffer' },
  { key: 'IGNORED', label: 'Ignoriert' },
];

const CONFIDENCE_COLOR = {
  'Sehr hoch': '#22c55e',
  'Hoch': '#4ade80',
  'Mittel': '#f59e0b',
  'Niedrig': '#fb923c',
  'Sehr niedrig': '#f87171',
};

const STATUS_COLOR = {
  UPLOADED: '#64748b',
  PROCESSING: '#f59e0b',
  DONE: '#22c55e',
  ERROR: '#f87171',
};

function fmtEur(cents) {
  if (cents == null) return '—';
  return (cents / 100).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
}

function fmtDate(d) {
  if (!d) return '—';
  return d.slice(0, 10);
}

// ─── Upload Section ───────────────────────────────────────────────────────────
function UploadSection({ onUploaded }) {
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const fileRef = useRef();

  async function handleFiles(files) {
    if (!files || files.length === 0) return;
    setError(null);
    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        const fd = new FormData();
        fd.append('file', file);
        await bankStatementsApi.upload(fd);
      }
      onUploaded();
    } catch (e) {
      if (e.response?.status === 409) {
        setError(e.response?.data?.message || 'Dieser Kontoauszug wurde bereits hochgeladen.');
      } else {
        setError(e.response?.data?.error || e.response?.data?.message || e.message || 'Upload fehlgeschlagen');
      }
    } finally {
      setUploading(false);
    }
  }

  return (
    <div style={{ marginBottom: 24 }}>
      <div
        onDragOver={e => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={e => { e.preventDefault(); setDragging(false); handleFiles(e.dataTransfer.files); }}
        onClick={() => fileRef.current?.click()}
        style={{
          border: `2px dashed ${dragging ? '#6366f1' : '#334155'}`,
          borderRadius: 12, padding: '32px 24px', textAlign: 'center',
          cursor: 'pointer', transition: 'border-color 0.2s',
          background: dragging ? 'rgba(99,102,241,0.08)' : 'transparent',
        }}
      >
        <div style={{ fontSize: 32, marginBottom: 8 }}>🏦</div>
        <div style={{ color: '#f1f5f9', fontWeight: 600, fontSize: 15 }}>
          {uploading ? 'Wird hochgeladen…' : 'Kontoauszug hochladen'}
        </div>
        <div style={{ color: '#64748b', fontSize: 12, marginTop: 4 }}>
          PDF oder Bild (max. 20 MB) — per Klick oder Drag & Drop
        </div>
        <input ref={fileRef} type="file" accept=".pdf,image/*" multiple
          style={{ display: 'none' }}
          onChange={e => handleFiles(e.target.files)} />
      </div>
      {error && (
        <div style={{ marginTop: 8, color: '#f87171', fontSize: 13, padding: '8px 12px',
          background: 'rgba(248,113,113,0.1)', borderRadius: 8 }}>
          {error}
        </div>
      )}
    </div>
  );
}

// ─── Statement List ───────────────────────────────────────────────────────────
function StatementList({ statements, onRetried }) {
  const [retrying, setRetrying] = useState({});

  async function handleRetry(id) {
    setRetrying(r => ({ ...r, [id]: true }));
    try {
      await bankStatementsApi.retry(id);
      if (onRetried) onRetried();
    } catch (_) {
      if (onRetried) onRetried();
    } finally {
      setRetrying(r => ({ ...r, [id]: false }));
    }
  }

  if (!statements || statements.length === 0) return (
    <div style={{ color: '#475569', fontSize: 13, textAlign: 'center', padding: 24 }}>
      Noch keine Kontoauszüge hochgeladen.
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 24 }}>
      {statements.map(s => (
        <div key={s.id} style={{ background: '#1e293b', border: '1px solid #334155',
          borderRadius: 10, padding: '12px 16px', display: 'flex', justifyContent: 'space-between',
          alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <div>
            <div style={{ color: '#f1f5f9', fontSize: 14, fontWeight: 600 }}>{s.original_filename}</div>
            <div style={{ color: '#64748b', fontSize: 11, marginTop: 2 }}>
              {s.bank_name && <span style={{ marginRight: 8 }}>{s.bank_name}</span>}
              {s.account_iban_masked && <span style={{ marginRight: 8 }}>{s.account_iban_masked}</span>}
              {s.statement_from && <span>{fmtDate(s.statement_from)} – {fmtDate(s.statement_to)}</span>}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
            {s.transaction_count > 0 && (
              <span style={{ color: '#94a3b8', fontSize: 12 }}>{s.transaction_count} Buchungen</span>
            )}
            {s.candidate_count > 0 && (
              <span style={{ color: '#818cf8', fontSize: 12 }}>{s.candidate_count} Kandidaten</span>
            )}
            <span style={{ fontSize: 11, fontWeight: 600, color: STATUS_COLOR[s.status] || '#64748b',
              background: 'rgba(100,116,139,0.15)', padding: '2px 8px', borderRadius: 4 }}>
              {s.status}
            </span>
            {s.status === 'ERROR' && (
              <button
                onClick={() => handleRetry(s.id)}
                disabled={retrying[s.id]}
                className="btn-primary">
                {retrying[s.id] ? 'Lädt…' : 'Erneut verarbeiten'}
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Summary Cards ────────────────────────────────────────────────────────────
function SummaryCards({ summary }) {
  if (!summary) return null;
  const cards = [
    { label: 'Kontoauszüge', value: summary.statement_count, color: '#818cf8' },
    { label: 'Ausstehend', value: summary.pending_candidates, color: '#94a3b8' },
    { label: 'Neue Verträge', value: summary.new_candidates, color: '#22c55e' },
    { label: 'Erkannt', value: summary.existing_matches, color: '#3b82f6' },
    { label: 'Preisabw.', value: summary.price_deviations, color: '#f59e0b' },
  ];
  return (
    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 24 }}>
      {cards.map(c => (
        <div key={c.label} style={{ background: '#1e293b', border: '1px solid #334155',
          borderRadius: 10, padding: '12px 16px', minWidth: 80, textAlign: 'center', flex: '1 1 80px' }}>
          <div style={{ fontSize: 22, fontWeight: 700, color: c.color }}>{c.value ?? 0}</div>
          <div style={{ fontSize: 10, color: '#64748b', marginTop: 2 }}>{c.label}</div>
        </div>
      ))}
    </div>
  );
}

// ─── Candidate Card ───────────────────────────────────────────────────────────
function CandidateCard({ candidate, onAction }) {
  const [expanded, setExpanded] = useState(false);
  const [acting, setActing] = useState(false);
  const [actionError, setActionError] = useState(null);
  const c = candidate;

  async function doAction(action) {
    setActing(true);
    setActionError(null);
    try {
      if (action === 'ignore') await bankCandidatesApi.ignore(c.id);
      else if (action === 'create') await bankCandidatesApi.createContract(c.id);
      else if (action === 'applyFields') await bankCandidatesApi.applyFields(c.id);
      onAction();
    } catch (e) {
      setActionError(e.response?.data?.error || e.message || 'Fehler');
      setActing(false);
    }
  }

  const confidenceColor = CONFIDENCE_COLOR[c.confidence_label] || '#64748b';
  const reasons = (() => { try { return JSON.parse(c.confidence_reasons_json || '[]'); } catch { return []; } })();
  const proposed = (() => { try { return JSON.parse(c.proposed_fields_json || '{}'); } catch { return {}; } })();
  const deviation = c.price_deviation_json
    ? (() => { try { return JSON.parse(c.price_deviation_json); } catch { return null; } })()
    : null;

  return (
    <div style={{ background: '#1e293b', border: `1px solid ${c.result_type === 'NEW_CONTRACT_CANDIDATE' ? '#22c55e44' : '#334155'}`,
      borderRadius: 12, padding: 16, marginBottom: 12 }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ color: '#f1f5f9', fontWeight: 700, fontSize: 15, marginBottom: 2 }}>{c.title}</div>
          {c.counterparty && (
            <div style={{ color: '#94a3b8', fontSize: 12 }}>{c.counterparty}</div>
          )}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, flexShrink: 0, marginLeft: 12 }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: confidenceColor,
            background: 'rgba(0,0,0,0.3)', padding: '2px 8px', borderRadius: 4 }}>
            {c.confidence_label}
          </span>
          <span style={{ fontSize: 10, color: '#475569' }}>Score: {c.score}</span>
        </div>
      </div>

      {/* Key facts */}
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 8 }}>
        {c.amount_cents != null && (
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#f1f5f9' }}>{fmtEur(c.amount_cents)}</div>
            <div style={{ fontSize: 10, color: '#64748b' }}>{c.interval || 'Betrag'}</div>
          </div>
        )}
        {c.transaction_count > 0 && (
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#818cf8' }}>{c.transaction_count}×</div>
            <div style={{ fontSize: 10, color: '#64748b' }}>Buchungen</div>
          </div>
        )}
        {c.first_payment_date && (
          <div>
            <div style={{ fontSize: 12, color: '#94a3b8' }}>{fmtDate(c.first_payment_date)}</div>
            <div style={{ fontSize: 10, color: '#64748b' }}>Erste Buchung</div>
          </div>
        )}
        {c.last_payment_date && c.last_payment_date !== c.first_payment_date && (
          <div>
            <div style={{ fontSize: 12, color: '#94a3b8' }}>{fmtDate(c.last_payment_date)}</div>
            <div style={{ fontSize: 10, color: '#64748b' }}>Letzte Buchung</div>
          </div>
        )}
      </div>

      {/* Price deviation warning */}
      {deviation && (
        <div style={{ background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)',
          borderRadius: 8, padding: '8px 12px', marginBottom: 8 }}>
          <div style={{ color: '#f59e0b', fontSize: 12, fontWeight: 600 }}>Preisabweichung</div>
          <div style={{ color: '#94a3b8', fontSize: 11 }}>
            Vertrag: {fmtEur(deviation.contract_amount_cents)} → Buchung: {fmtEur(deviation.statement_amount_cents)}
            {deviation.deviation_pct != null && ` (+${deviation.deviation_pct.toFixed(1)}%)`}
          </div>
        </div>
      )}

      {/* Expand toggle */}
      <button onClick={() => setExpanded(v => !v)}
        className="btn-ghost" style={{ padding: '4px 0', marginBottom: 8 }}>
        {expanded ? '▲ Weniger' : '▼ Details'}
      </button>

      {expanded && (
        <div style={{ marginBottom: 12 }}>
          {/* Confidence reasons */}
          {reasons.length > 0 && (
            <div style={{ marginBottom: 8 }}>
              <div style={{ color: '#64748b', fontSize: 10, marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Erkennungsmerkmale
              </div>
              {reasons.map((r, i) => (
                <div key={i} style={{ color: '#94a3b8', fontSize: 11, marginBottom: 2 }}>• {r}</div>
              ))}
            </div>
          )}
          {/* Proposed fields */}
          {Object.keys(proposed).length > 0 && (
            <div>
              <div style={{ color: '#64748b', fontSize: 10, marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Zu ergänzende Felder
              </div>
              {Object.entries(proposed).map(([k, v]) => (
                <div key={k} style={{ color: '#94a3b8', fontSize: 11, marginBottom: 2 }}>
                  <span style={{ color: '#475569' }}>{k}: </span>{String(v)}
                </div>
              ))}
            </div>
          )}
          {/* IDs */}
          {(c.vertragsnummer || c.kundennummer || c.mandatsreferenz || c.creditor_id) && (
            <div style={{ marginTop: 8 }}>
              <div style={{ color: '#64748b', fontSize: 10, marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Kenndaten
              </div>
              {c.vertragsnummer && <div style={{ color: '#94a3b8', fontSize: 11 }}>Vertrags-Nr.: {c.vertragsnummer}</div>}
              {c.kundennummer && <div style={{ color: '#94a3b8', fontSize: 11 }}>Kunden-Nr.: {c.kundennummer}</div>}
              {c.mandatsreferenz && <div style={{ color: '#94a3b8', fontSize: 11 }}>Mandatsreferenz: {c.mandatsreferenz}</div>}
              {c.creditor_id && <div style={{ color: '#94a3b8', fontSize: 11 }}>Creditor-ID: {c.creditor_id}</div>}
            </div>
          )}
        </div>
      )}

      {/* Actions */}
      {c.status === 'PENDING' && (() => {
        const needsReview = c.requires_review === 1 || c.requires_review === true || c.score < 70;
        if (needsReview && !expanded) {
          return (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <button onClick={() => setExpanded(true)}
                style={{ background: '#f59e0b', color: '#000', border: 'none', borderRadius: 6,
                  padding: '6px 14px', cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>
                ▼ Prüfen
              </button>
              <span style={{ color: '#64748b', fontSize: 11 }}>Bitte Details prüfen, bevor ein Vertrag angelegt wird.</span>
            </div>
          );
        }
        return (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {c.result_type === 'NEW_CONTRACT_CANDIDATE' && (
              <button onClick={() => doAction('create')} disabled={acting}
                style={{ background: needsReview ? '#475569' : '#22c55e', color: needsReview ? '#fff' : '#000',
                  border: needsReview ? '1px solid #64748b' : 'none', borderRadius: 6,
                  padding: '6px 14px', cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>
                Als neuen Vertrag anlegen
              </button>
            )}
            {(c.result_type === 'EXISTING_CONTRACT' || c.result_type === 'REVIEW_REQUIRED') &&
              Object.keys(proposed).length > 0 && (
              <button onClick={() => doAction('applyFields')} disabled={acting}
                style={{ background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 6,
                  padding: '6px 14px', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                Felder ergänzen
              </button>
            )}
            <button onClick={() => doAction('ignore')} disabled={acting}
              className="btn-secondary">
              Ignorieren
            </button>
          </div>
        );
      })()}
      {c.status === 'CREATED' && (
        <div style={{ color: '#22c55e', fontSize: 12 }}>✓ Vertrag angelegt</div>
      )}
      {c.status === 'IGNORED' && (
        <div style={{ color: '#475569', fontSize: 12 }}>Ignoriert</div>
      )}
      {actionError && (
        <div style={{ color: '#f87171', fontSize: 12, marginTop: 6 }}>{actionError}</div>
      )}
    </div>
  );
}

// ─── Candidates Tab ───────────────────────────────────────────────────────────
function CandidatesTab({ resultType, onAction }) {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    const filters = {};
    if (resultType === 'IGNORED') filters.status = 'IGNORED';
    else { filters.result_type = resultType; filters.status = 'PENDING'; }
    bankCandidatesApi.list(filters)
      .then(data => setCandidates(data || []))
      .catch(() => setCandidates([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [resultType]); // eslint-disable-line

  if (loading) return <div style={{ color: '#475569', textAlign: 'center', padding: 32 }}>Lädt…</div>;
  if (candidates.length === 0) return (
    <div style={{ color: '#475569', textAlign: 'center', padding: 32 }}>
      Keine Einträge in dieser Kategorie.
    </div>
  );

  return (
    <div>
      {candidates.map(c => (
        <CandidateCard key={c.id} candidate={c} onAction={() => { onAction(); load(); }} />
      ))}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function BankStatementPage() {
  const [tab, setTab] = useState('UEBERSICHT');
  const [statements, setStatements] = useState([]);
  const [summary, setSummary] = useState(null);
  const [summaryKey, setSummaryKey] = useState(0);

  const refreshSummary = () => setSummaryKey(k => k + 1);

  useEffect(() => {
    bankStatementsApi.list()
      .then(data => setStatements(data || []))
      .catch(() => {});
  }, [summaryKey]);

  useEffect(() => {
    bankStatementsApi.summary()
      .then(setSummary)
      .catch(() => {});
  }, [summaryKey]);

  const tabBtnStyle = (active) => ({
    background: active ? '#818cf8' : 'transparent',
    color: active ? '#fff' : '#64748b',
    border: active ? 'none' : '1px solid #334155',
    borderRadius: 6, padding: '6px 14px', cursor: 'pointer',
    fontSize: 12, fontWeight: active ? 700 : 400,
    whiteSpace: 'nowrap',
  });

  return (
    <div style={{ padding: '16px 16px 80px', maxWidth: 700, margin: '0 auto' }}>
      <h1 style={{ color: '#f1f5f9', fontSize: 20, fontWeight: 700, marginBottom: 4 }}>
        🏦 Kontoauszüge
      </h1>
      <p style={{ color: '#64748b', fontSize: 13, marginBottom: 20 }}>
        Lade Kontoauszüge hoch. Der Vertragsmanager erkennt automatisch wiederkehrende Zahlungen und Vertragsdaten.
        Keine Daten werden an externe KI-Dienste übertragen.
      </p>

      <UploadSection onUploaded={refreshSummary} />
      <SummaryCards summary={summary} />

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 6, overflowX: 'auto', marginBottom: 20, paddingBottom: 4 }}>
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} style={tabBtnStyle(tab === t.key)}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {tab === 'UEBERSICHT' && (
        <StatementList statements={statements} onRetried={refreshSummary} />
      )}
      {tab !== 'UEBERSICHT' && (
        <CandidatesTab resultType={tab} onAction={refreshSummary} />
      )}
    </div>
  );
}
